import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface DatePickerProps {
  value: string; // ISO yyyy-mm-dd
  onChange: (val: string) => void;
  disabled?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  const monthNames = [
    t('directAvailability.months.jan', 'enero'), t('directAvailability.months.feb', 'febrero'), t('directAvailability.months.mar', 'marzo'),
    t('directAvailability.months.apr', 'abril'), t('directAvailability.months.may', 'mayo'), t('directAvailability.months.jun', 'junio'),
    t('directAvailability.months.jul', 'julio'), t('directAvailability.months.aug', 'agosto'), t('directAvailability.months.sep', 'septiembre'),
    t('directAvailability.months.oct', 'octubre'), t('directAvailability.months.nov', 'noviembre'), t('directAvailability.months.dec', 'diciembre')
  ];

  const daysMin = [
    t('directAvailability.daysMin.mo', 'L'),
    t('directAvailability.daysMin.tu', 'M'),
    t('directAvailability.daysMin.we', 'M'),
    t('directAvailability.daysMin.th', 'J'),
    t('directAvailability.daysMin.fr', 'V'),
    t('directAvailability.daysMin.sa', 'S'),
    t('directAvailability.daysMin.su', 'D')
  ];

  // Safe parsing of yyyy-mm-dd into a local Date at noon to avoid timezone shift
  const parseISODate = (v: string) => {
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  };

  const [viewDate, setViewDate] = useState(() => {
    try {
      return parseISODate(value);
    } catch {
      return new Date();
    }
  });

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const valDate = parseISODate(value);
  const today = new Date();
  
  const displayLabel = `${String(valDate.getDate()).padStart(2, '0')}/${String(valDate.getMonth()+1).padStart(2, '0')}/${valDate.getFullYear()}`;

  const getDays = () => {
    const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1, 12);
    const offset = (first.getDay() || 7) - 1;
    const start = new Date(first);
    start.setDate(first.getDate() - offset);
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  return (
    <div className={`date-picker ${isOpen ? 'open' : ''}`} ref={ref}>
      <button 
        type="button" 
        className="date-picker-btn" 
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="date-picker-value">{displayLabel}</span><span>⌄</span>
      </button>
      <div className="date-picker-popover">
        <div className="date-picker-head">
          <strong className="date-picker-title">{`${monthNames[viewDate.getMonth()]} ${viewDate.getFullYear()}`}</strong>
          <div className="date-picker-nav">
            <button type="button" onClick={() => { const nd = new Date(viewDate); nd.setMonth(nd.getMonth() - 1); setViewDate(nd); }}>‹</button>
            <button type="button" onClick={() => { const nd = new Date(viewDate); nd.setMonth(nd.getMonth() + 1); setViewDate(nd); }}>›</button>
          </div>
        </div>
        <div className="date-week">
          {daysMin.map((d, i) => <span key={i}>{d}</span>)}
        </div>
        <div className="date-grid">
          {getDays().map((d, i) => {
            const isOut = d.getMonth() !== viewDate.getMonth();
            const isSelected = iso(d) === value;
            const isToday = iso(d) === iso(today);
            return (
              <button
                key={i}
                type="button"
                className={`date-day ${isOut ? 'out' : ''} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                onClick={() => {
                  onChange(iso(d));
                  setIsOpen(false);
                }}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
        <div className="date-picker-footer">
          <button 
            type="button" 
            className="date-picker-today"
            onClick={() => {
              const t = new Date();
              setViewDate(t);
              onChange(iso(t));
              setIsOpen(false);
            }}
          >
            {t('directAvailability.today', 'Hoy')}
          </button>
        </div>
      </div>
    </div>
  );
};
